import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../../Shared/colors';
import moment from 'moment-timezone';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';

const RaceReportPopup = ({ 
  isOpen, 
  onClose, 
  raceData = [],
  currentTime = null,
  currentUserId = null 
}) => {
  const [countdown, setCountdown] = useState(15);
  const [isClosing, setIsClosing] = useState(false);

  // Auto-dismiss countdown
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  // Reset countdown when popup opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(15);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  // Get rating badge and color
  const getRatingInfo = (score) => {
    if (score === 100) {
      return {
        icon: <EmojiEventsIcon />,
        text: 'Excellent',
        color: '#10b981',
        gradient: ['#10b981', '#059669'],
        badge: '🏆'
      };
    } else if (score === 90) {
      return {
        icon: <TrendingUpIcon />,
        text: 'Average',
        color: '#FABC0B',
        gradient: ['#FABC0B', '#e0a800'],
        badge: '⚡'
      };
    } else {
      return {
        icon: <WarningIcon />,
        text: 'Not Productive',
        color: '#E80B0B',
        gradient: ['#E80B0B', '#c82333'],
        badge: '📉'
      };
    }
  };

  // Get rank badge
  const getRankBadge = (rank) => {
    switch (rank) {
      case 1:
        return { emoji: '🥇', bg: '#FFD700', border: '#FFA500' };
      case 2:
        return { emoji: '🥈', bg: '#C0C0C0', border: '#A0A0A0' };
      case 3:
        return { emoji: '🥉', bg: '#CD7F32', border: '#A0522D' };
      default:
        return { emoji: rank, bg: colors.lightWhite, border: colors.inputBorderWhite };
    }
  };

  // Get rank background color
  const getRankBgColor = (rank) => {
    if (rank === 1) return 'linear-gradient(135deg, #FFF9E6 0%, #FFF4CC 100%)';
    if (rank === 2) return 'linear-gradient(135deg, #F5F5F5 0%, #E8E8E8 100%)';
    if (rank === 3) return 'linear-gradient(135deg, #FFF4E6 0%, #FFE8CC 100%)';
    return colors.lightWhite;
  };

  // Sort employees by score (descending), then by meetings (descending), then by name
  const sortedEmployees = [...raceData].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if ((b.meetingsCompleted || 0) !== (a.meetingsCompleted || 0)) return (b.meetingsCompleted || 0) - (a.meetingsCompleted || 0);
    return (a.employeeName || '').localeCompare(b.employeeName || '');
  });

  // Format time
  const displayTime = currentTime 
    ? moment(currentTime).tz('Asia/Karachi').format('h:mm A')
    : moment().tz('Asia/Karachi').format('h:mm A');

  if (!isOpen) return null;

  return (
    <RacePopupOverlay onClick={handleClose} isClosing={isClosing}>
      <RacePopupContainer 
        onClick={(e) => e.stopPropagation()}
        isClosing={isClosing}
      >
        {/* Header */}
        <RaceHeader>
          <HeaderContent>
            <RaceIcon>🏁</RaceIcon>
            <HeaderText>
              <Title>DAILY RACE UPDATE</Title>
              <TimeDisplay>{displayTime}</TimeDisplay>
            </HeaderText>
          </HeaderContent>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </RaceHeader>

        {/* Leaderboard */}
        <LeaderboardList>
          {sortedEmployees.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📊</EmptyIcon>
              <EmptyText>No race data available yet</EmptyText>
              <EmptySubtext>Check back in a few minutes!</EmptySubtext>
            </EmptyState>
          ) : (
            sortedEmployees.map((employee, index) => {
              const rank = employee.position || (index + 1);
              const rankBadge = getRankBadge(rank);
              const ratingInfo = getRatingInfo(employee.score);
              const isCurrentUser = currentUserId && employee.employeeId === currentUserId;

              return (
                <LeaderboardItem
                  key={employee.employeeId || index}
                  rank={rank}
                  isCurrentUser={isCurrentUser}
                  style={{
                    background: getRankBgColor(rank),
                    borderLeftColor: rankBadge.border
                  }}
                >
                  <RankBadgeContainer rank={rank}>
                    <RankBadge rank={rank} style={{ background: rankBadge.bg }}>
                      {rankBadge.emoji}
                    </RankBadge>
                  </RankBadgeContainer>

                  <EmployeeInfo>
                    <EmployeeName isCurrentUser={isCurrentUser}>
                      {employee.employeeName || 'Unknown Employee'}
                      {isCurrentUser && <YouBadge>You</YouBadge>}
                    </EmployeeName>
                    <StatsRow>
                      <Score>{employee.score} pts</Score>
                      <RatingBadge color={ratingInfo.color}>
                        {ratingInfo.badge} {ratingInfo.text}
                      </RatingBadge>
                    </StatsRow>
                    <ActivityStats>
                      <ActivityItem>
                        <ActivityIcon>📞</ActivityIcon>
                        <ActivityText>
                          <ActivityLabel>Connected Calls</ActivityLabel>
                          <ActivityValue>{employee.connectedCalls || 0}</ActivityValue>
                        </ActivityText>
                      </ActivityItem>
                      <ActivityItem>
                        <ActivityIcon>🤝</ActivityIcon>
                        <ActivityText>
                          <ActivityLabel>Meetings</ActivityLabel>
                          <ActivityValue>{employee.meetingsCompleted || 0}</ActivityValue>
                        </ActivityText>
                      </ActivityItem>
                    </ActivityStats>
                  </EmployeeInfo>

                  <ScoreBarContainer>
                    <ScoreBar
                      score={employee.score}
                      gradient={ratingInfo.gradient}
                      rank={rank}
                    />
                    <ScorePercentage>{employee.score}%</ScorePercentage>
                  </ScoreBarContainer>
                </LeaderboardItem>
              );
            })
          )}
        </LeaderboardList>

        {/* Footer */}
        <RaceFooter>
          <CountdownText>
            {countdown > 0 ? `Closing in ${countdown}s` : 'Closing...'}
          </CountdownText>
          <FooterActions>
            <ActionButton onClick={handleClose}>
              Close
            </ActionButton>
          </FooterActions>
        </RaceFooter>
      </RacePopupContainer>
    </RacePopupOverlay>
  );
};

export default RaceReportPopup;

// Animations
const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const slideDown = keyframes`
  0% {
    transform: translateY(-100px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`;

const fillBar = keyframes`
  0% { width: 0; }
  100% { width: ${props => props.score}%; }
`;

// Styled Components
const RacePopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${props => props.isClosing ? fadeOut : fadeIn} 0.3s ease;
`;

const RacePopupContainer = styled.div`
  background: ${colors.white};
  border-radius: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${props => props.isClosing ? fadeOut : slideDown} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, 
      ${colors.brown} 0%, 
      ${colors.textBlue} 50%, 
      ${colors.brown} 100%
    );
    z-index: 1;
  }
`;

const RaceHeader = styled.div`
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RaceIcon = styled.span`
  font-size: 2rem;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Title = styled.h2`
  color: ${colors.white};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 1px;
`;

const TimeDisplay = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: ${colors.white};
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const LeaderboardList = styled.div`
  flex: 1;
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.lightWhite};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.brown};
    border-radius: 3px;
    
    &:hover {
      background: #1a6b6b;
    }
  }
`;

const LeaderboardItem = styled.div`
  background: ${props => props.rank <= 3 ? 'transparent' : colors.lightWhite};
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-left: 4px solid;
  box-shadow: ${props => props.rank <= 3 ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'};
  transition: all 0.3s ease;
  position: relative;
  ${props => props.isCurrentUser && `
    border: 2px solid ${colors.brown};
    box-shadow: 0 0 0 3px rgba(36, 147, 147, 0.1);
  `}
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 6px 16px rgba(0,0,0,0.15);
  }
`;

const RankBadgeContainer = styled.div`
  flex-shrink: 0;
`;

const RankBadge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.rank <= 3 ? '1.5rem' : '1rem'};
  font-weight: 700;
  color: ${props => props.rank <= 3 ? colors.white : colors.text};
  background: ${props => props.rank <= 3 ? props.style?.background : colors.lightWhite};
  border: 2px solid ${props => props.rank <= 3 ? 'transparent' : colors.inputBorderWhite};
  box-shadow: ${props => props.rank <= 3 ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'};
`;

const EmployeeInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const EmployeeName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.isCurrentUser ? colors.brown : colors.text};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const YouBadge = styled.span`
  background: ${colors.brown};
  color: ${colors.white};
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Score = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${colors.text};
`;

const RatingBadge = styled.span`
  background: ${props => props.color}15;
  color: ${props => props.color};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ScoreBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  min-width: 100px;
`;

const ScoreBar = styled.div`
  width: 100px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.score}%;
    background: linear-gradient(90deg, ${props => props.gradient[0]}, ${props => props.gradient[1]});
    border-radius: 4px;
    animation: ${fillBar} 0.8s ease-out;
  }
`;

const ScorePercentage = styled.span`
  font-size: 0.75rem;
  color: ${colors.textMed};
  font-weight: 500;
`;

const ActivityStats = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${colors.lightWhite};
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${colors.inputBorderWhite};
`;

const ActivityIcon = styled.span`
  font-size: 1.125rem;
  line-height: 1;
`;

const ActivityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const ActivityLabel = styled.span`
  font-size: 0.625rem;
  font-weight: 500;
  color: ${colors.textMed};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActivityValue = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${colors.text};
`;

const RaceFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${colors.inputBorderWhite};
  background: ${colors.lightWhite};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CountdownText = styled.span`
  font-size: 0.875rem;
  color: ${colors.textMed};
  font-weight: 500;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1.5rem;
  border: 1px solid ${colors.brown};
  border-radius: 8px;
  background: ${colors.brown};
  color: ${colors.white};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #1a6b6b;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(36, 147, 147, 0.3);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 0.5rem;
`;

const EmptySubtext = styled.div`
  font-size: 0.875rem;
  color: ${colors.textMed};
`;
